import { useState, useCallback } from 'react';

import { generatePath, useNavigate } from 'react-router-dom';

import { useMutation } from '@zenbusiness/application-commons-graphql';
import { useCaptureException } from '@zenbusiness/application-commons-logging';

import * as ROUTES from '#client/constants/routes';
import { CREATE_THREAD_MUTATION } from '#client/mutations/createThreadMutation';

/**
 * Custom hook for anonymous thread creation
 * @returns {object}
 * @property {boolean} loading - Whether the thread creation is in progress
 * @property {object} error - The error object
 * @property {number} retryCount - The number of times the thread creation has been retried
 * @property {object} customError - The custom error object
 * @property {Function} createNewThread - The function to create a new thread
 * @property {Function} retryThreadCreation - The function to retry the thread creation
 * @property {Function} clearError - The function to clear the error
 * @property {boolean} canRetry - Whether the thread creation can be retried
 * @property {boolean} isNetworkError - Whether the thread creation is a network error
 * @property {boolean} isAuthError - Whether the thread creation is an authentication error
 */
export function useVisitorThreadCreation() {
  const [retryCount, setRetryCount] = useState(0);
  const [customError, setCustomError] = useState(null);

  const navigate = useNavigate();

  // Initialize captureException with static tags for better performance
  const captureException = useCaptureException({
    tags: {
      frontend: 'assistant-ui',
      operationName: 'createThread'
    }
  });

  const [createThreadMutation, { loading, error }] = useMutation(CREATE_THREAD_MUTATION);

  /**
   * Creates a new thread and redirects to thread viewer
   * @param {string} overrideBusinessEntityUuid - Optional override for business entity UUID
   * @param {object} initialInput - Optional initial input to override default greeting
   * @returns {Promise<{threadUuid: string, messages: Array}>}
   */
  const createNewThread = useCallback(
    async (visitUuid, initialInput = {}, options = { redirect: true }) => {
      // Clear any previous custom errors
      setCustomError(null);

      if (!visitUuid) {
        const errorMsg = new Error('Visit UUID is required for thread creation');
        captureException(errorMsg, {
          tags: {
            errorType: 'validation_error',
            visitUuid
          },
          contexts: {
            threadCreation: {
              visitUuid
            }
          }
        });
        setCustomError(errorMsg);
        throw errorMsg;
      }

      try {
        // Create default input or use provided initial input
        // TODO: remove this once we have a proper topic parameter or a default message for thread creation
        const defaultInput = {
          visitUuid,
          agentName: 'business-idea-generation-form',
          message: 'Hello! I want to get business ideas for my business.',
          messageType: 'invoke',
          role: 'user'
        };

        const input = initialInput || defaultInput;

        const result = await createThreadMutation({
          variables: {
            identity: {
              visitor: {
                visitUuid
              }
            },
            input
          },
          onError: (err) => {
            const isNetworkError =
              err.message?.includes('fetch') ||
              err.message?.includes('Network') ||
              err.networkError;

            captureException(err, {
              tags: {
                errorType: isNetworkError ? 'network_error' : 'graphql_mutation',
                visitUuid
              },
              contexts: {
                threadCreation: {
                  retryCount,
                  visitUuid,
                  isNetworkError,
                  wasLoading: loading
                },
                mutationError: {
                  message: err.message,
                  networkError: err.networkError,
                  graphQLErrors: err.graphQLErrors
                }
              }
            });
          }
        });

        const threadData = result?.data?.createThread;
        const newThreadUuid = threadData?.threadUuid;

        // Check if the response contains an error (like rate limiting)
        if (threadData?.error) {
          const responseError = new Error(threadData.error.message);
          responseError.code = threadData.error.code;
          responseError.statusCode = threadData.error.statusCode;
          responseError.data = { createThread: threadData }; // Include the full response for error checking

          captureException(responseError, {
            tags: {
              errorType:
                threadData.error.code === 'RATE_LIMITED' ? 'rate_limited' : 'response_error',
              visitUuid,
              errorCode: threadData.error.code
            },
            contexts: {
              threadCreation: {
                visitUuid,
                retryCount,
                responseError: threadData.error,
                statusCode: threadData.error.statusCode
              }
            }
          });

          setCustomError(responseError);
          throw responseError;
        }

        if (!newThreadUuid) {
          const error1 = new Error('Thread creation failed: No thread UUID returned');
          captureException(error1, {
            tags: {
              errorType: 'invalid_response',
              visitUuid
            },
            contexts: {
              threadCreation: {
                visitUuid,
                retryCount,
                responseData: threadData,
                hasResult: !!result,
                hasData: !!result?.data,
                hasCreateThread: !!result?.data?.createThread
              }
            }
          });
          throw error1; // Fixed: was 'error', now 'error1'
        }

        // Reset retry count on success
        setRetryCount(0);

        if (options.redirect) {
          // Redirect to thread viewer with navigation state
          navigate(generatePath(ROUTES.visitorThread, { threadUuid: newThreadUuid }), {
            replace: true,
            state: {
              fromThreadCreation: true,
              visitUuid,
              isInitialLoad: true,
              threadData: {
                threadUuid: newThreadUuid,
                createdAt: new Date().toISOString()
              }
            }
          });
        }

        return {
          threadUuid: newThreadUuid,
          data: threadData.data || []
        };
      } catch (err) {
        const isNetworkError =
          err.message?.includes('fetch') || err.message?.includes('Network') || err.networkError;

        captureException(err, {
          tags: {
            errorType: isNetworkError ? 'network_error' : 'thread_creation_failed',
            visitUuid
          },
          contexts: {
            threadCreation: {
              visitUuid,
              retryCount,
              errorMessage: err.message,
              isNetworkError
            }
          }
        });

        setCustomError(err);
        throw err;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createThreadMutation, error, navigate, retryCount]
  );

  /**
   * Retry thread creation with exponential backoff
   */
  const retryThreadCreation = useCallback(
    async (visitUuid, initialInput = null) => {
      const newRetryCount = retryCount + 1;
      setRetryCount(newRetryCount);

      // Exponential backoff: 1s, 2s, 4s, 8s, max 8s
      const delay = Math.min(1000 * 2 ** (newRetryCount - 1), 8000);

      // Add delay before retrying to implement exponential backoff
      await new Promise((resolve) => {
        setTimeout(resolve, delay);
      });
      return createNewThread(visitUuid, initialInput);
    },
    [retryCount, createNewThread]
  );

  /**
   * Reset error state
   */
  const clearError = useCallback(() => {
    setRetryCount(0);
  }, []);

  return {
    // State
    loading,
    error,
    retryCount,
    customError,

    // Actions
    createNewThread,
    retryThreadCreation,
    clearError,

    // Computed values
    canRetry: retryCount < 3 && !loading, // Max 3 retries
    isNetworkError: Boolean(
      error?.message?.includes('fetch') ||
        error?.message?.includes('Network') ||
        error?.networkError
    ),
    isAuthError: Boolean(
      error?.message?.includes('401') || error?.message?.includes('authentication')
    )
  };
}
